import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Form,
  Header,
  Loader,
  Modal,
  Statistic,
  Table,
} from "semantic-ui-react";
import { API, showError } from "../../helpers";
import { renderNumber } from "../../helpers/render";
import "./CompanyBilling.css";

const fmtMonth = (d) => {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const previousMonth = () => {
  const now = new Date();
  return fmtMonth(new Date(now.getFullYear(), now.getMonth() - 1, 1));
};

const monthRange = (month) => {
  const [year, mon] = month.split("-").map(Number);
  const lastDay = new Date(year, mon, 0).getDate();
  return [`${month}-01`, `${month}-${String(lastDay).padStart(2, "0")}`];
};

const fmtAmount = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
};

const csvCell = (value) => {
  const text = String(value ?? "");
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const downloadCSV = (filename, rows) => {
  const csv = rows.map((row) => row.map(csvCell).join(",")).join("\r\n");
  const blob = new Blob([`\ufeff${csv}`], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const statementCSV = (statement) => {
  const rows = [
    ["对账月份", statement.month],
    ["公司", statement.company_name],
    [],
    ["汇总", "输入Token", "输出Token", "总Token", "请求数", "费用(元)"],
    [
      "高峰",
      statement.peak_prompt_tokens,
      statement.peak_completion_tokens,
      statement.peak_tokens,
      statement.peak_requests,
      fmtAmount(statement.peak_charge),
    ],
    [
      "非高峰",
      statement.idle_prompt_tokens,
      statement.idle_completion_tokens,
      statement.idle_tokens,
      statement.idle_requests,
      fmtAmount(statement.idle_charge),
    ],
    [
      "合计",
      statement.prompt_tokens,
      statement.completion_tokens,
      statement.total_tokens,
      statement.requests,
      fmtAmount(statement.charge),
    ],
    [],
    [
      "模型明细",
      "高峰输入",
      "高峰输出",
      "高峰Token",
      "高峰费用",
      "非高峰输入",
      "非高峰输出",
      "非高峰Token",
      "非高峰费用",
      "合计Token",
      "合计费用",
    ],
  ];

  statement.models.forEach((m) => {
    rows.push([
      m.model_name,
      m.peak_prompt_tokens,
      m.peak_completion_tokens,
      m.peak_tokens,
      fmtAmount(m.peak_charge),
      m.idle_prompt_tokens,
      m.idle_completion_tokens,
      m.idle_tokens,
      fmtAmount(m.idle_charge),
      m.total_tokens,
      fmtAmount(m.charge),
    ]);
  });
  return rows;
};

const CompanyBilling = () => {
  const [month, setMonth] = useState(previousMonth);
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(true);
  const [statements, setStatements] = useState([]);
  const [peakHours, setPeakHours] = useState("08:00 - 20:00");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    if (!/^\d{4}-\d{2}$/.test(month)) {
      showError('请选择对账月份');
      return;
    }
    const [t1, t2] = monthRange(month);
    setLoading(true);
    setSelected(null);
    try {
      const res = await API.get("/api/dashboard/billing/statements", {
        params: { t1, t2 },
      });
      if (res.data.success) {
        setStatements(res.data.data?.statements || []);
        const start = res.data.data?.peak_start_hour ?? 8;
        const end = res.data.data?.peak_end_hour ?? 20;
        const pad = (n) => String(n).padStart(2, "0");
        setPeakHours(
          `${pad(start)}:00 - ${pad(end)}:00${end < start ? "（次日）" : ""}`
        );
      } else {
        showError(res.data.message);
      }
    } catch (e) {
      showError(e);
    }
    setLoading(false);
  }, [month]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredStatements = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    if (!kw) return statements;
    return statements.filter((s) =>
      (s.company_name || "").toLowerCase().includes(kw)
    );
  }, [keyword, statements]);

  const totals = useMemo(() => {
    return filteredStatements.reduce(
      (acc, s) => ({
        charge: acc.charge + Number(s.charge || 0),
        tokens: acc.tokens + Number(s.total_tokens || 0),
        companies: acc.companies + 1,
      }),
      { charge: 0, tokens: 0, companies: 0 }
    );
  }, [filteredStatements]);

  const downloadStatement = (statement) => {
    const safeName = (statement.company_name || "未分配").replace(
      /[\\/:*?"<>|]/g,
      "_"
    );
    downloadCSV(
      `${statement.month}_${safeName}_对账单.csv`,
      statementCSV(statement)
    );
  };

  return (
    <div className="dashboard-container">
      <Card fluid className="chart-card">
        <Card.Content>
          <Header as="h3">
            公司月度对账单
            <Header.Subheader>
            高峰时段：{peakHours} · 计费口径：Token × 模型价格配置
          </Header.Subheader>
          </Header>
          <div className="billing-toolbar">
            <Form.Input
              type="month"
              label="对账月份"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            />
            <Form.Input
              label="公司搜索"
              icon="search"
              placeholder="输入公司名称"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
            <Button onClick={load}>刷新</Button>
          </div>
          <Statistic.Group widths="three" size="tiny">
            <Statistic>
              <Statistic.Value>{fmtAmount(totals.charge)}</Statistic.Value>
              <Statistic.Label>总费用 (元)</Statistic.Label>
            </Statistic>
            <Statistic>
              <Statistic.Value>{renderNumber(totals.tokens)}</Statistic.Value>
              <Statistic.Label>总 Token</Statistic.Label>
            </Statistic>
            <Statistic>
              <Statistic.Value>{totals.companies}</Statistic.Value>
              <Statistic.Label>使用公司</Statistic.Label>
            </Statistic>
          </Statistic.Group>
        </Card.Content>
      </Card>

      {loading ? (
        <Loader active>加载中...</Loader>
      ) : (
        <Card fluid className="chart-card">
          <Card.Content>
            <div className="billing-table-wrap">
              <Table celled selectable compact>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>公司</Table.HeaderCell>
                    <Table.HeaderCell>请求数</Table.HeaderCell>
                    <Table.HeaderCell>总 Token</Table.HeaderCell>
                    <Table.HeaderCell>高峰 Token</Table.HeaderCell>
                    <Table.HeaderCell>非高峰 Token</Table.HeaderCell>
                    <Table.HeaderCell>总费用 (元)</Table.HeaderCell>
                    <Table.HeaderCell>高峰费用 (元)</Table.HeaderCell>
                    <Table.HeaderCell>非高峰费用 (元)</Table.HeaderCell>
                    <Table.HeaderCell>模型数</Table.HeaderCell>
                    <Table.HeaderCell>操作</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {filteredStatements.length === 0 ? (
                    <Table.Row>
                      <Table.Cell colSpan={10} textAlign="center">
                        暂无数据
                      </Table.Cell>
                    </Table.Row>
                  ) : (
                    filteredStatements.map((s) => (
                      <Table.Row
                        key={`${s.company_id}-${s.month}`}
                        onClick={() => setSelected(s)}
                        style={{ cursor: "pointer" }}
                      >
                        <Table.Cell>{s.company_name || "未分配"}</Table.Cell>
                        <Table.Cell>{renderNumber(s.requests)}</Table.Cell>
                        <Table.Cell>{renderNumber(s.total_tokens)}</Table.Cell>
                        <Table.Cell>{renderNumber(s.peak_tokens)}</Table.Cell>
                        <Table.Cell>{renderNumber(s.idle_tokens)}</Table.Cell>
                        <Table.Cell>{fmtAmount(s.charge)}</Table.Cell>
                        <Table.Cell>{fmtAmount(s.peak_charge)}</Table.Cell>
                        <Table.Cell>{fmtAmount(s.idle_charge)}</Table.Cell>
                        <Table.Cell>{s.models?.length || 0}</Table.Cell>
                        <Table.Cell>
                          <Button
                            size="tiny"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelected(s);
                            }}
                          >
                            查看对账单
                          </Button>
                        </Table.Cell>
                      </Table.Row>
                    ))
                  )}
                </Table.Body>
              </Table>
            </div>
          </Card.Content>
        </Card>
      )}

      <Modal open={!!selected} onClose={() => setSelected(null)} size="large">
        <Modal.Header>
          {selected
            ? `${selected.month} · ${selected.company_name || "未分配"} 对账单`
            : ""}
        </Modal.Header>
        <Modal.Content>
          {selected && (
            <>
              <Statistic.Group widths="four" size="tiny">
                <Statistic>
                  <Statistic.Value>
                    {fmtAmount(selected.charge)}
                  </Statistic.Value>
                  <Statistic.Label>总费用 (元)</Statistic.Label>
                </Statistic>
                <Statistic>
                  <Statistic.Value>
                    {renderNumber(selected.total_tokens)}
                  </Statistic.Value>
                  <Statistic.Label>总 Token</Statistic.Label>
                </Statistic>
                <Statistic>
                  <Statistic.Value>
                    {fmtAmount(selected.peak_charge)}
                  </Statistic.Value>
                  <Statistic.Label>高峰费用 (元)</Statistic.Label>
                </Statistic>
                <Statistic>
                  <Statistic.Value>
                    {fmtAmount(selected.idle_charge)}
                  </Statistic.Value>
                  <Statistic.Label>非高峰费用 (元)</Statistic.Label>
                </Statistic>
              </Statistic.Group>
              <div className="billing-table-wrap billing-model-table">
                <Table celled compact>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>模型</Table.HeaderCell>
                      <Table.HeaderCell>高峰输入</Table.HeaderCell>
                      <Table.HeaderCell>高峰输出</Table.HeaderCell>
                      <Table.HeaderCell>高峰 Token</Table.HeaderCell>
                      <Table.HeaderCell>高峰费用 (元)</Table.HeaderCell>
                      <Table.HeaderCell>非高峰输入</Table.HeaderCell>
                      <Table.HeaderCell>非高峰输出</Table.HeaderCell>
                      <Table.HeaderCell>非高峰 Token</Table.HeaderCell>
                      <Table.HeaderCell>非高峰费用 (元)</Table.HeaderCell>
                      <Table.HeaderCell>合计 Token</Table.HeaderCell>
                      <Table.HeaderCell>合计费用 (元)</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {(selected.models || []).map((m) => (
                      <Table.Row key={m.model_name}>
                        <Table.Cell>{m.model_name}</Table.Cell>
                        <Table.Cell>
                          {renderNumber(m.peak_prompt_tokens)}
                        </Table.Cell>
                        <Table.Cell>
                          {renderNumber(m.peak_completion_tokens)}
                        </Table.Cell>
                        <Table.Cell>{renderNumber(m.peak_tokens)}</Table.Cell>
                        <Table.Cell>{fmtAmount(m.peak_charge)}</Table.Cell>
                        <Table.Cell>
                          {renderNumber(m.idle_prompt_tokens)}
                        </Table.Cell>
                        <Table.Cell>
                          {renderNumber(m.idle_completion_tokens)}
                        </Table.Cell>
                        <Table.Cell>{renderNumber(m.idle_tokens)}</Table.Cell>
                        <Table.Cell>{fmtAmount(m.idle_charge)}</Table.Cell>
                        <Table.Cell>{renderNumber(m.total_tokens)}</Table.Cell>
                        <Table.Cell>{fmtAmount(m.charge)}</Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </div>
              <div className="billing-modal-actions">
                <Button positive onClick={() => downloadStatement(selected)}>
                  下载 CSV 对账单
                </Button>
              </div>
            </>
          )}
        </Modal.Content>
      </Modal>
    </div>
  );
};

export default CompanyBilling;
